<template>
  <div class="dashboard">
    <LoadingOverlay
      :visible="resolving || isSyncingLibrary || (loading && !!selectedSteamId)"
      :title="overlayTitle"
      :message="overlayMessage"
      :tips="overlayTips"
      :profile-name="overlayProfileName"
      :profile-avatar="overlayProfileAvatar"
      :profile-subtitle="overlayProfileSubtitle"
    />
    <ClaimConfirmedNotice />
    <ClaimCard
      v-if="showMandatoryClaim"
      :profile-steam-id="selectedSteamId"
      :lookup-key="lastSteamIdentifier"
      forced
      :dismissible="mandatoryClaimReason !== 'new_lookup'"
      :existing-profile="mandatoryClaimReason === 'unclaimed_profile'"
      :reason="mandatoryClaimReason"
      @email-submitted="onMandatoryClaimEmailSubmitted"
    />
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>{{ $t('status.loadingPlaytime') }}</p>
    </div>

    <div v-else-if="error" class="error-container">
      <div class="alert alert-danger" role="alert" aria-live="assertive">
        <span class="alert-ico" aria-hidden="true">⚠️</span>
        <span class="alert-text">{{ error }}</span>
      </div>
    </div>

    <div v-else class="dashboard-content">
      <DashboardLanding
        v-if="!selectedSteamId"
        v-model:steamInput="steamInput"
        :resolving="resolving"
        :steam-error="steamError"
        :steam-success="steamSuccess"
        :yesterday-total-minutes="yesterdayTotalMinutes"
        :yesterday-achievements="yesterdayAchievements"
        :yesterday-unique-apps="yesterdayUniqueApps"
        :yesterday-top-games="yesterdayTopGames"
        :yesterday-top-users="yesterdayTopUsers"
        :yesterday-top-achievers="yesterdayTopAchievers"
        :format-playtime="formatPlaytime"
        @submit="resolveSteamId"
      />

      <div v-else>
        <div class="main-layout">
          <DashboardSummarySection
            v-if="!shouldHideDataSections"
            :date-range-type="dateRangeType"
            :suspend-charts="suspendCharts"
            :metrics="summaryMetrics"
            :range-start-label="activeRangeLabels.start"
            :range-end-label="activeRangeLabels.end"
            :profile="userProfile"
            :profile-steam-id="selectedSteamId"
            @update:dateRangeType="handleDateRangeChange"
            @exit-profile="exitProfile"
          />

          <div class="content-area">
            <DashboardReplaySection
              v-if="!showZeroSinceStartNotice"
              :playtime-data="filteredData"
              :range-start="filterRangeStart"
              :range-end="filterRangeEnd"
              :app-name-by-id="appNameById"
              :app-types="appTypes"
              :achievements="achievements"
              :get-game-image-url="getGameImageUrl"
            />

            <DashboardChartsSection
              :suspend-charts="suspendCharts"
              :show-zero-since-start-notice="showZeroSinceStartNotice"
              :newly-registered="newlyRegistered"
              :user-profile="userProfile"
              :profile-steam-id="selectedSteamId"
              :top-games-preview="topGamesPreview"
              :chart-data="chartData"
              :stacked-chart-data="stackedChartData"
              :active-date-range="activeDateRange"
              :format-obj="formatObj"
              :format-playtime="formatPlaytime"
              :format-date-label="formatChartDateLabel"
              :get-game-image-url="getGameImageUrl"
              :resolve-preview-game-name="resolvePreviewGameName"
              :range-empty="isRangeEmpty"
            />

            <GamesActivitySection
              v-if="!showZeroSinceStartNotice"
              :games="paginatedGames"
              :pagination-info="paginationInfo"
              :sort-by="sortBy"
              :sort-order="sortOrder"
              :games-per-page="gamesPerPage"
              :total-pages="totalPages"
              :current-page="currentPage"
              :visible-pages="visiblePages"
              :format-playtime="formatPlaytime"
              :display-playtime="displayPlaytime"
              :format-date="formatDate"
              :get-game-name="getGameName"
              :get-game-image-url="getGameImageUrl"
              :get-steam-store-url="getSteamStoreUrl"
              :achievements-by-app="achievementsByApp"
              @update:sortBy="onSortBySelect"
              @toggle-sort-order="toggleSortOrder"
              @update:gamesPerPage="onGamesPerPageChange"
              @sort-games="sortGames"
              @go-to-page="goToPage"
              @set-sort-by="setSortBy"
            />

            <!-- Fetches its own data, and only once scrolled into view. An
                 unregistered shell has no row to ask about. -->
            <AchievementHistorySection
              v-if="!showZeroSinceStartNotice && profileRegistered"
              :steam-id="selectedSteamId"
              :claimed="typeof userProfile.claimed === 'boolean' ? userProfile.claimed : null"
              :start-date="filterRangeStart || ''"
              :end-date="filterRangeEnd || ''"
              :format-date="formatDate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { playtimeAPI } from '../services/api'
import LoadingOverlay from '../components/LoadingOverlay.vue'
import DashboardLanding from '../components/dashboard/DashboardLanding.vue'
import DashboardSummarySection from '../components/dashboard/DashboardSummarySection.vue'
import DashboardChartsSection from '../components/dashboard/DashboardChartsSection.vue'
import GamesActivitySection from '../components/dashboard/GamesActivitySection.vue'
import DashboardReplaySection from '../components/dashboard/DashboardReplaySection.vue'
import AchievementHistorySection from '../components/dashboard/AchievementHistorySection.vue'
import { format, fromUnixTime } from 'date-fns'
import { applyMeta, SITE_ORIGIN } from '../utils/meta'
import ClaimCard from '../components/dashboard/ClaimCard.vue'
import ClaimConfirmedNotice from '../components/dashboard/ClaimConfirmedNotice.vue'
import { isMandatoryClaimPending, setMandatoryClaimPending } from '../utils/claimGate'
import { trackEvent } from '../utils/analytics'

const normalizeProfile = (profile = {}) => {
  const normalized = { ...profile }
  normalized.display_name = normalized.display_name || ''
  normalized.avatar_url = normalized.avatar_url || ''
  normalized.profile_url = normalized.profile_url || ''

  if (Object.prototype.hasOwnProperty.call(profile, 'isSynced')) {
    normalized.isSynced = profile.isSynced
  } else if (Object.prototype.hasOwnProperty.call(profile, 'is_synced')) {
    normalized.isSynced = profile.is_synced
  } else if (typeof normalized.isSynced !== 'boolean') {
    normalized.isSynced = false
  }

  return normalized
}

const BROWSE_REFS = ['top-players', 'top-achievers', 'example']
const DEFAULT_PAGE_TITLE = 'Steam Playtime Tracker — Charts, Trends & Game Insights | steamplayti.me'
const PROFILE_TITLE_SUFFIX = 'Steam Playtime Tracker | steamplayti.me'

export default {
  name: 'Dashboard',
  // Lets the optional ClaimCards in the sections stay shut while the forced
  // one above them is on screen.
  provide() {
    return { isClaimGateOpen: () => this.showMandatoryClaim }
  },
  components: {
    LoadingOverlay,
    DashboardLanding,
    DashboardSummarySection,
    DashboardChartsSection,
    GamesActivitySection,
    DashboardReplaySection,
    AchievementHistorySection,
    ClaimCard,
    ClaimConfirmedNotice
  },
  data() {
    return {
      loading: true,
      error: null,
      playtimeData: [],
      appNameById: {},
      selectedSteamId: '',

      // The mandatory claim gate (see utils/claimGate.js). isNewLookup is true
      // only for the specific lookup that just triggered a first-ever sync
      // (never for a returning/existing profile), which is what scopes the
      // hard gate to brand-new visitors only.
      // mandatoryClaimPending/mandatoryClaimSteamId are deliberately NOT
      // derived from isSyncingLibrary: the background sync finishes on its
      // own schedule regardless of the claim modal, and gating on sync state
      // let the modal vanish the moment sync completed even though nothing
      // was claimed. This flag only ever clears on an explicit email submit
      // (or a fresh lookup superseding it), never on sync progress.
      // mandatoryClaimReason mirrors the GA param: 'new_lookup' is the hard
      // gate; 'pending_return' and 'unclaimed_profile' (a profile looked up
      // before the 2026-09-02 claim gate and never claimed) are closable.
      isNewLookup: false,
      // False when the API answered `registered: false` — the profile has never
      // been claimed, so no row, no library and no sync exist for it yet. The
      // dashboard has nothing to render and nothing to poll; the claim gate is
      // the whole screen.
      profileRegistered: true,
      mandatoryClaimPending: false,
      mandatoryClaimSteamId: '',
      mandatoryClaimReason: '',
      
      suspendCharts: false,
      effectiveRange: { start: null, end: null },
      
      sortBy: 'recent',
      sortOrder: 'desc',
      currentPage: 1,
      gamesPerPage: 5,
      steamInput: '',
      lastSteamIdentifier: '',
      resolving: false,
      steamError: null,
      steamSuccess: null,
      userProfile: normalizeProfile(),
      
      // Initial fallback range. Replaced by inferDefaultRangeFromAge() once
      // the user profile loads — see also `dateRangeManuallySet`.
      dateRangeType: '90d',
      // Set to true once the user picks a range from the dropdown. From then
      // on we never overwrite their choice with the inferred default, even
      // when the profile changes.
      dateRangeManuallySet: false,

      currentIdentifier: null,
      pendingRouteUpdate: null,
      logoutInProgress: false,
      profileRequestId: 0,
      yesterdayTotalsPromise: null,

      yesterdayTotalMinutes: null,
      yesterdayTotalDate: '',
      yesterdayAchievements: null,
      yesterdayUniqueApps: null,
      yesterdayTopGames: [],
      yesterdayTopUsers: [],
      yesterdayTopAchievers: [],
      syncPollTimer: null,
      // Whether we have stopped waiting for Steam. The overlay used to be
      // derived from the profile alone, so when the poll gave up nothing
      // lowered it: the user sat on a full-screen spinner with nobody polling
      // behind it, and the copy explaining why lives in DashboardLanding, which
      // is v-if'd away as soon as a profile is selected.
      //
      // It only ever ENDS a wait, never starts one — paths that raise the
      // overlay without polling (a profile pushed in by steamProfileUpdated)
      // behave exactly as before.
      syncWaitEnded: false,

      topGamesPreview: {
        loading: false,
        loaded: false,
        error: null,
        games: [],
        snapshotDate: '',
        source: '',
        totalGames: 0,
        achievements: { total: 0, icons: [] },
        steamId: ''
      },
      topGamesPreviewRequestId: 0,

      // appTypes ships in the diff response now (filter-scoped to visible apps).
      // Kept separate so DashboardReplaySection can compute streak / monthly /
      // type aggregates reactively from filteredData + this map.
      appTypes: {},

      // Header-image OVERRIDES from the diff response, app_id → url. Only holds
      // apps whose art can't be derived from the standard header path; getGameImageUrl
      // falls back to the derived URL for anything absent here.
      appImagesById: {},

      // Date-range-scoped "Last Unlocked" achievements card, from the diff
      // response (exact count + last-10 icons, computed server-side).
      achievements: { unlockedCount: 0, items: [] },
      // Per-game achievement strips for the games table: { appId: { count, icons } }.
      achievementsByApp: {}
    }
  },
  beforeUnmount() {
    window.removeEventListener('steamProfileUpdated', this._profileHandler)
    this.clearSyncPoll()
  },
  watch: {
    '$route.params.steamId': {
      immediate: true,
      handler(newId) {
        this.handleRouteChange(newId)
      }
    },
    // Watches title + canonical together on purpose: applyMeta() always writes
    // the full merged set, so a second watcher calling it with only a canonical
    // would reset the title back to DEFAULT_META.
    metaState: {
      immediate: true,
      handler({ title, canonical }) {
        if (typeof document === 'undefined') {
          return
        }

        applyMeta({
          title,
          ogTitle: title,
          twitterTitle: title,
          canonical,
          ogUrl: canonical,
        })
      }
    }
  },
  computed: {
    // Arrived by clicking a profile on the landing page (DashboardLanding's
    // `ref` links): somebody browsing, not the owner, so the visit must not
    // count as their login. Any other navigation drops the query.
    isBrowseVisit() {
      return BROWSE_REFS.includes(this.$route?.query?.ref)
    },
    isSyncingLibrary() {
      // Nothing is being fetched for an unclaimed profile — saying otherwise
      // would put "Syncing your library" under a modal that is the reason no
      // sync has started.
      if (!this.profileRegistered) return false
      if (this.syncWaitEnded) return false
      return !!(this.selectedSteamId && (!this.userProfile || !this.userProfile.isSynced))
    },
    showMandatoryClaim() {
      if (!this.mandatoryClaimPending) return false
      // An unregistered vanity has no Steam ID yet — resolving one would mean
      // calling Steam for a visitor who has claimed nothing. The gate still
      // opens; Steam's own login hands us the proven id on the way back.
      if (!this.profileRegistered) return true

      return this.selectedSteamId === this.mandatoryClaimSteamId
    },
    overlayTitle() {
      if (this.resolving) return this.$t('overlay.resolvingTitle');
      if (this.isSyncingLibrary) return this.$t('overlay.syncingTitle');
      if (this.loading && this.selectedSteamId) return this.$t('overlay.loadingTitle');
      return this.$t('common.loading');
    },
    overlayMessage() {
      if (this.resolving) return this.$t('overlay.resolvingMessage');
      if (this.isSyncingLibrary) return this.$t('overlay.syncingMessage');
      if (this.loading && this.selectedSteamId) return this.$t('overlay.loadingMessage');
      return '';
    },
    overlayTips() {
      if (this.isSyncingLibrary) return this.$t('overlay.syncTip');
      return '';
    },
    overlayProfileName() {
      if (!this.isSyncingLibrary) return '';
      const displayName = typeof this.userProfile?.display_name === 'string'
        ? this.userProfile.display_name.trim()
        : '';
      if (displayName) return displayName;

      const identifier = typeof this.currentIdentifier === 'string'
        ? this.currentIdentifier.trim()
        : '';
      if (identifier) return identifier;

      const steamId = typeof this.selectedSteamId === 'string'
        ? this.selectedSteamId.trim()
        : '';
      return steamId;
    },
    overlayProfileAvatar() {
      if (!this.isSyncingLibrary) return '';
      const avatar = typeof this.userProfile?.avatar_url === 'string'
        ? this.userProfile.avatar_url.trim()
        : '';
      return avatar;
    },
    overlayProfileSubtitle() {
      if (!this.isSyncingLibrary) return '';
      const displayName = typeof this.userProfile?.display_name === 'string'
        ? this.userProfile.display_name.trim()
        : '';
      const identifier = typeof this.currentIdentifier === 'string'
        ? this.currentIdentifier.trim()
        : '';
      const steamId = typeof this.selectedSteamId === 'string'
        ? this.selectedSteamId.trim()
        : '';
      const primary = displayName || identifier || steamId;
      if (displayName && identifier && identifier.toLowerCase() !== displayName.toLowerCase()) {
        return identifier;
      }
      if (steamId && steamId !== primary) {
        return this.$t('overlay.steamIdLabel', { id: steamId });
      }
      return '';
    },
    // Every /user/* page used to inherit DEFAULT_META.canonical — i.e. it told
    // Google it was a duplicate of the homepage, which is the likeliest reason
    // those pages draw impressions but essentially no clicks. A profile is also
    // reachable as both /user/<vanity> and /user/<steamId>, so prefer the
    // resolved numeric id and let the vanity URL point at it.
    canonicalUrl() {
      const routeParam = typeof this.$route?.params?.steamId === 'string'
        ? this.$route.params.steamId.trim()
        : ''

      if (!routeParam) {
        return `${SITE_ORIGIN}/`
      }

      const resolved = typeof this.selectedSteamId === 'string' ? this.selectedSteamId.trim() : ''

      return `${SITE_ORIGIN}/user/${encodeURIComponent(resolved || routeParam)}`
    },
    metaState() {
      return { title: this.pageTitle, canonical: this.canonicalUrl }
    },
    pageTitle() {
      const displayName = typeof this.userProfile?.display_name === 'string'
        ? this.userProfile.display_name.trim()
        : ''
      const identifier = typeof this.currentIdentifier === 'string'
        ? this.currentIdentifier.trim()
        : ''
      const steamId = typeof this.selectedSteamId === 'string'
        ? this.selectedSteamId.trim()
        : ''
      const routeParam = typeof this.$route?.params?.steamId === 'string'
        ? this.$route.params.steamId.trim()
        : ''

      const label = displayName || identifier || steamId || routeParam
      if (!label) {
        return DEFAULT_PAGE_TITLE
      }

      return `${label} — ${PROFILE_TITLE_SUFFIX}`
    },
    yesterdayDateLabel() {
      if (!this.yesterdayTotalDate) return ''
      const fallback = typeof this.yesterdayTotalDate === 'string' ? this.yesterdayTotalDate : ''
      return this.formatDate(this.yesterdayTotalDate, { fallback, variant: 'long' })
    },
    filterRangeStart() {
      const range = (this.effectiveRange && (this.effectiveRange.start || this.effectiveRange.end)) ? this.effectiveRange : this.activeDateRange
      return this.sanitizeDiffDate(range.start)
    },
    filterRangeEnd() {
      const range = (this.effectiveRange && (this.effectiveRange.start || this.effectiveRange.end)) ? this.effectiveRange : this.activeDateRange
      return this.sanitizeDiffDate(range.end)
    },
    todayISO() {
      try {
        return this.normalizeToUtcDateKey(new Date())
      } catch (e) {
        return ''
      }
    },
    trackingEndDate() {
      const today = new Date()
      if (!today || Number.isNaN(today.getTime())) return null
      today.setUTCHours(0, 0, 0, 0)
      const end = new Date(today)
      end.setUTCDate(end.getUTCDate() - 1)
      if (!end || Number.isNaN(end.getTime())) return null
      return end
    },
    
    earliestDateISO() {
      if (!this.playtimeData.length) return ''
      const normalized = this.playtimeData
        .map(rec => (typeof rec.date === 'string' ? rec.date : this.sanitizeDiffDate(rec.date)))
        .filter(Boolean)
        .sort()
      return normalized.length ? normalized[0] : ''
    },
    displayRangeLabel() {
      switch (this.dateRangeType) {
        case 'thisWeek':
          return this.$t('summary.ranges.thisWeek')
        case 'thisMonth':
          return this.$t('summary.ranges.thisMonth')
        case '7d':
          return this.$t('summary.ranges.lastWeek')
        case '14d':
          return this.$t('summary.ranges.lastTwoWeeks')
        case '30d':
          return this.$t('summary.ranges.last30')
        case '365d':
          return this.$t('summary.ranges.last365')
        case '90d':
        default:
          return this.$t('summary.ranges.last90')
      }
    },
    userLocale() {
      try {
        const resolved = Intl.DateTimeFormat().resolvedOptions()?.locale
        return resolved || 'en-US'
      } catch (error) {
        return 'en-US'
      }
    },
    activeRangeLabels() {
      const range = this.activeDateRange || {}

      const formatBoundary = key => {
        const iso = this.sanitizeDiffDate(key)
        if (!iso) return ''

        const parts = iso.split('-')
        if (parts.length !== 3) return ''

        const [yearStr, monthStr, dayStr] = parts
        const year = Number.parseInt(yearStr, 10)
        const month = Number.parseInt(monthStr, 10)
        const day = Number.parseInt(dayStr, 10)

        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
          return ''
        }

        const locale = this.userLocale
        const twoDigit = new Intl.NumberFormat(locale, {
          minimumIntegerDigits: 2,
          maximumFractionDigits: 0,
          useGrouping: false
        })
        const fourDigit = new Intl.NumberFormat(locale, {
          minimumIntegerDigits: 4,
          maximumFractionDigits: 0,
          useGrouping: false
        })

        return `${twoDigit.format(day)}.${twoDigit.format(month)}.${fourDigit.format(year)}`
      }

      return {
        start: formatBoundary(range.start),
        end: formatBoundary(range.end)
      }
    },
    // Start of the data we actually hold, which is the registration date unless
    // the user went dormant and came back — deactivation deletes everything, so
    // a returning user's history starts at their return, not at signup.
    // created_at stays the membership date and is not a valid floor for data.
    dataStartsAt() {
      return this.userProfile?.tracking_since || this.userProfile?.created_at || null
    },

    isRegisteredToday() {
      const start = this.dataStartsAt
      if (!start) return false
      const startKey = this.normalizeToUtcDateKey(start)
      const todayKey = this.normalizeToUtcDateKey(new Date())
      return !!startKey && startKey === todayKey
    },
    newlyRegistered() {
      return this.isRegisteredToday
    },
    hasAnyRecordedPlaytime() {
      if (!Array.isArray(this.playtimeData)) return false;
      return this.playtimeData.some(entry => (entry.playtime_forever || 0) > 0);
    },
    shouldHideDataSections() {
      return this.showZeroSinceStartNotice && !this.hasAnyRecordedPlaytime;
    },
    showZeroSinceStartNotice() {
      // Only show the landing empty state for newly registered profiles with no data yet
      if (!this.newlyRegistered) return false
      return this.totalPlaytime === 0
    },
    isRangeEmpty() {
      if (!Array.isArray(this.playtimeData)) return true
      return this.totalPlaytime === 0
    },
    summaryMetrics() {
      const totalPlaytimeValue = this.formatPlaytime(this.totalPlaytime || 0)
      const avgDaily = this.formatPlaytime(this.avgDailyPlaytime || 0)
      const topGame = this.topGame || {}
      const rawName = typeof topGame.name === 'string' ? topGame.name.trim() : ''
      const topGameName = rawName || topGame.app_id || 'N/A'
      const topGamePlaytime = this.formatPlaytime(topGame.playtime || 0)

      // Playtime + Most Played track the active date range. All-Time does
      // not: it is Steam's own lifetime total (sum of hours on record from the
      // latest snapshot), so it covers play from before tracking began too.
      const topGameAppId = topGame.app_id
      const hasValidAppId = topGameAppId !== undefined && topGameAppId !== null
                && topGameAppId !== 'N/A' && /^\d+$/.test(String(topGameAppId))

      const metrics = [
        {
          id: 'playtime',
          icon: '⏱️',
          label: this.$t('summary.playtime'),
          value: totalPlaytimeValue,
          sub: this.$t('summary.avgPerDay', { avg: avgDaily })
        },
        {
          id: 'most-played',
          icon: '📈',
          label: this.$t('summary.mostPlayed'),
          value: topGameName,
          sub: topGamePlaytime,
          imageUrl: hasValidAppId ? this.getGameImageUrl(topGameAppId) : ''
        }
      ]

      // Hidden rather than shown as 0: a zero here means Steam is hiding the
      // playtime, not that nothing was played.
      const allTimeHours = Number(this.userProfile?.totalHoursWasted) || 0
      if (allTimeHours > 0) {
        const games = Number(this.userProfile?.totalNumberOfGames) || 0
        const numberLocale = this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US'
        metrics.push({
          id: 'all-time',
          icon: '🏆',
          label: this.$t('summary.allTime'),
          value: this.$t('common.hoursOnly', { h: Math.round(allTimeHours).toLocaleString(numberLocale) }),
          sub: this.$tc('summary.acrossGames', games, { count: games.toLocaleString(numberLocale) })
        })
      }

      return metrics
    },
    filteredData() {
      if (!this.selectedSteamId) return []
      
      let data = [...this.playtimeData]
      
      
      const range = (this.effectiveRange && (this.effectiveRange.start || this.effectiveRange.end)) ? this.effectiveRange : this.activeDateRange
      const { start, end } = range
      const startKey = start instanceof Date && !Number.isNaN(start.getTime()) ? this.formatObj(start, 'yyyy-MM-dd') : this.sanitizeDiffDate(start)
      const endKey = end instanceof Date && !Number.isNaN(end.getTime()) ? this.formatObj(end, 'yyyy-MM-dd') : this.sanitizeDiffDate(end)
      if (startKey || endKey) {
        data = data.filter(rec => {
          const recordKey = this.sanitizeDiffDate(rec.date)
          if (!recordKey) return false
          if (startKey && recordKey < startKey) return false
          if (endKey && recordKey > endKey) return false
          return true
        })
      }
      
      return data
    },
    isAllRange() {
      return this.dateRangeType === 'all'
    },
    activeDateRange() {
      const baseRange = this.computeBaseDateRange()
      let startKey = this.sanitizeDiffDate(baseRange.start)
      let endKey = this.sanitizeDiffDate(baseRange.end)

      const allKeys = this.playtimeData
        .map(r => (typeof r.date === 'string' ? this.sanitizeDiffDate(r.date) : this.extractDateKey(r.date)))
        .filter(Boolean)
        .sort()

      if (allKeys.length) {
        const earliestKey = allKeys[0]
        const latestKey = allKeys[allKeys.length - 1]

        if (!startKey) {
          startKey = earliestKey
        } else if (this.dateRangeType === 'all' && startKey < earliestKey) {
          startKey = earliestKey
        }

        if (!endKey) {
          endKey = latestKey
        } else if (this.dateRangeType === 'all' && endKey < latestKey) {
          endKey = latestKey
        }
      }

      if (!startKey && endKey) {
        startKey = endKey
      }
      if (startKey && endKey && startKey > endKey) {
        startKey = endKey
      }

      return {
        start: startKey,
        end: endKey
      }
    },
    
    
    chartData() {
      const all = Array.isArray(this.playtimeData) ? [...this.playtimeData] : []
      if (all.length === 0) return []

      const perDay = new Map()
      for (const record of all) {
        const key = typeof record.date === 'string' ? record.date : this.extractDateKey(record.date)
        if (!key) continue
        const total = (perDay.get(key) || 0) + (record.playtime_forever || 0)
        perDay.set(key, total)
      }

      return [...perDay.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
        .map(([date, total]) => ({ date, playtime_forever: total }))
    },
    stackedChartData() {
      const all = Array.isArray(this.playtimeData) ? [...this.playtimeData] : []
      if (all.length === 0) return []

      return all
        .map(record => ({
          date: typeof record.date === 'string' ? record.date : this.extractDateKey(record.date),
          app_id: record.app_id,
          playtime_forever: record.playtime_forever || 0,
          game_name: this.getGameName(record.app_id)
        }))
        .filter(entry => entry.date && entry.playtime_forever > 0)
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    },

    uniqueGames() {
      if (!this.selectedSteamId) return 0
      return new Set(this.playtimeData.map(item => item.app_id)).size
    },
    totalPlaytime() {
      return this.gamesList.reduce((sum, game) => sum + game.daily_playtime, 0)
    },
    uniqueDays() {
      if (!this.selectedSteamId) return 0
      return new Set(this.playtimeData.map(item => item.date)).size
    },
    daysTracked() {
      // Profile-lifetime stat: number of days we have been tracking, not the
      // active filter window. Counts from dataStartsAt rather than created_at,
      // so a returning user isn't told we have 200 days of data we deleted.
      const today = new Date()
      if (!today || Number.isNaN(today.getTime())) return 0
      today.setUTCHours(0, 0, 0, 0)

      const endDate = new Date(today.getTime())
      endDate.setUTCDate(endDate.getUTCDate() - 1)
      if (!endDate || Number.isNaN(endDate.getTime())) return 0

      const startKey = this.sanitizeDiffDate(this.dataStartsAt)
      let initialDate = this.toUtcDateFromKey(startKey)

      if (!initialDate && this.playtimeData.length) {
        const sortedDates = this.playtimeData
          .map(record => this.toUtcDateFromKey(record.date))
          .filter(date => date instanceof Date && !Number.isNaN(date.getTime()))
          .sort((a, b) => a - b)
        if (sortedDates.length) {
          initialDate = sortedDates[0]
        }
      }

      if (!initialDate) return 0
      if (initialDate > endDate) return 0

      const span = this.calculateUtcDaySpan(initialDate, endDate)
      return Math.max(span, 0)
    },
    
    
    gamesList() {
      if (!this.selectedSteamId) return []
      const gamesMap = new Map()
      const range = (this.effectiveRange && (this.effectiveRange.start || this.effectiveRange.end)) ? this.effectiveRange : this.activeDateRange
      const { start, end } = range
      const startKey = this.sanitizeDiffDate(start)
      const endKey = this.sanitizeDiffDate(end)
      const startTs = this.normalizeToUtcMidnight(startKey)
      const endTs = this.normalizeToUtcMidnight(endKey)
      for (const record of this.playtimeData) {
        const appId = record.app_id
        const mins = Number(record.playtime_forever) || 0
        if (!gamesMap.has(appId)) {
          gamesMap.set(appId, {
            app_id: appId,
            total_playtime_db: 0, 
            daily_playtime: 0,    
            days_played: 0,
            last_activity_date: null,
            last_activity_ts: null,
            _perDay: new Map()
          })
        }
        const game = gamesMap.get(appId)
        game.total_playtime_db += mins

        const recordKey = this.sanitizeDiffDate(record.date)
        const recordTs = this.normalizeToUtcMidnight(recordKey)
        if (!recordKey || recordTs === null) {
          continue
        }

        if (startTs !== null && recordTs < startTs) continue
        if (endTs !== null && recordTs > endTs) continue

        const prev = game._perDay.get(recordKey) || 0
        const next = prev + mins
        game._perDay.set(recordKey, next)
        game.daily_playtime += mins
        if (next > 0) {
          if (!game.last_activity_ts || recordTs > game.last_activity_ts) {
            game.last_activity_ts = recordTs
            game.last_activity_date = recordKey
          }
        }
      }
      
      gamesMap.forEach(g => {
        g.days_played = Array.from(g._perDay.values()).filter(v => v > 0).length
        delete g._perDay
        if (!g.last_activity_date) {
          g.last_activity_date = ''
          g.last_activity_ts = null
        } else if (typeof g.last_activity_ts !== 'number' || Number.isNaN(g.last_activity_ts)) {
          g.last_activity_ts = null
        }
      })
      return Array.from(gamesMap.values())
    },
    
    sortedGames() {
      
      const games = [...this.gamesList]
      const order = this.sortOrder === 'desc' ? 1 : -1
      
      const recentCmp = (a, b) => {
        const ad = typeof a.last_activity_ts === 'number' ? a.last_activity_ts : 0
        const bd = typeof b.last_activity_ts === 'number' ? b.last_activity_ts : 0
        return bd - ad 
      }
      return games.sort((a, b) => {
        let cmp = 0
        switch (this.sortBy) {
          case 'name': {
            const nameA = this.getGameName(a.app_id).toLowerCase()
            const nameB = this.getGameName(b.app_id).toLowerCase()
            cmp = nameA.localeCompare(nameB) * order
            break
          }
          case 'daily_playtime': {
            const av = this.getComparablePlaytime(a)
            const bv = this.getComparablePlaytime(b)
            cmp = (bv - av) * order
            break
          }
          case 'days':
            cmp = (b.days_played - a.days_played) * order
            break
          case 'recent':
            cmp = recentCmp(a, b) * (this.sortOrder === 'desc' ? 1 : -1)
            break
          default:
            cmp = 0
        }
        if (cmp !== 0) return cmp
        
        const tie = recentCmp(a, b)
        if (tie !== 0) return tie
        
        return a.app_id - b.app_id
      })
    },
    
    paginatedGames() {
      const start = (this.currentPage - 1) * this.gamesPerPage
      const end = start + this.gamesPerPage
      return this.sortedGames.slice(start, end)
    },
    
    totalPages() {
      return Math.ceil(this.sortedGames.length / this.gamesPerPage)
    },
    
    paginationInfo() {
      const start = (this.currentPage - 1) * this.gamesPerPage + 1
      const end = Math.min(this.currentPage * this.gamesPerPage, this.sortedGames.length)
      return {
        start,
        end,
        total: this.sortedGames.length
      }
    },
    
    activeGames() {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const cutoff = oneWeekAgo.getTime();
      return this.gamesList.filter(game => {
        const ts = typeof game.last_activity_ts === 'number' ? game.last_activity_ts : null
        if (ts === null) return false
        return ts > cutoff
      }).length
    },
    
    topGame() {
      if (this.gamesList.length === 0) return { name: 'None', app_id: 'N/A', playtime: 0 }
      const top = this.gamesList.reduce((max, game) => 
        game.daily_playtime > max.daily_playtime ? game : max
      )
      return {
        name: this.getGameName(top.app_id),
        app_id: top.app_id,
        playtime: top.daily_playtime
      }
    },
    
    avgDailyPlaytime() {
      if (this.dateRangeType === 'all') {
        const denom = Math.max(this.daysTracked, 1)
        return Math.round(this.totalPlaytime / denom)
      }
      const { start, end } = this.activeDateRange
      if (!start || !end) return 0
      const days = this.calculateUtcDaySpan(start, end)
      if (days <= 0) return 0
      return Math.round(this.totalPlaytime / Math.max(days, 1))
    },

    displayedDaysTracked() {
      return Math.max(this.daysTracked, 0)
    },
    
    recentGamesCount() {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const cutoff = oneWeekAgo.getTime();
      return this.gamesList.filter(game => {
        const ts = typeof game.last_activity_ts === 'number' ? game.last_activity_ts : null
        if (ts === null) return false
        return ts > cutoff
      }).length
    },
    
    earliestDate() {
      const fallback = 'N/A'
      if (this.dataStartsAt) {
        const parsedProfile = this.parseDateInput(this.dataStartsAt)
        if (parsedProfile) {
          return this.formatDate(this.dataStartsAt, { fallback, variant: 'long', includeTime: false })
        }
      }
      if (this.playtimeData.length === 0) return fallback
      const earliest = this.playtimeData.reduce((min, item) => (item.date < min ? item.date : min), this.playtimeData[0].date)
      return this.formatDate(earliest, { fallback, variant: 'long' })
    },
    


    visiblePages() {
      const maxVisible = 5
      const pages = []
      
      if (this.totalPages <= maxVisible) {
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i)
        }
      } else {
        const start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
        const end = Math.min(this.totalPages, start + maxVisible - 1)
        
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }
      }
      
      return pages
    }
  },
  async mounted() {
    this._profileHandler = (e) => {
      const detail = e.detail || {}
      if (detail.source === 'dashboard') return

      const sid = typeof detail.steamId === 'string' ? detail.steamId.trim() : ''

      if (!sid) {
        this.handleExternalLogout()
        return
      }

      if (detail.profile) {
        this.userProfile = normalizeProfile(detail.profile)
      }
      if (sid && sid !== this.selectedSteamId) {
        this.selectedSteamId = sid
      }
    }
    window.addEventListener('steamProfileUpdated', this._profileHandler)
  },
  methods: {
    onMandatoryClaimEmailSubmitted() {
      setMandatoryClaimPending(this.mandatoryClaimSteamId, false)
      this.mandatoryClaimPending = false
      this.mandatoryClaimSteamId = ''
      this.mandatoryClaimReason = ''
      // The GA event lives in ClaimCard.submitEmail: only this instance has
      // the listener, so reporting from here missed every claim made from the
      // two optional cards.
    },
    async exitProfile() {
      // Wired from the profile card's ✕ button. Mirrors the old App.vue
      // exitProfile path: clear local state, dispatch a steamProfileUpdated
      // event with empty payload (so any other listener hears the logout),
      // and navigate back to the landing route.
      try {
        window.dispatchEvent(new CustomEvent('steamProfileUpdated', {
          detail: { profile: { display_name: '', avatar_url: '', profile_url: '', isSynced: false }, steamId: '', source: 'dashboard' }
        }))
      } catch (_) {}
      try {
        await this.navigateToIdentifier('')
      } catch (_) {}
      await this.resetToLanding({ fetchStats: true })
    },
    handleDateRangeChange(value) {
      const next = typeof value === 'string' ? value : ''
      if (!next) return
      this.dateRangeType = next
      this.dateRangeManuallySet = true
      this.applyFilters()
    },
    inferDefaultRangeFromAge(createdAt) {
      // Pick a default that won't show a mostly-empty chart. Thresholds tuned
      // so the picked range is comfortably filled by the user's tracked
      // history — e.g. only escalate to 365d once they have ≈3 months of data.
      if (!createdAt) return null
      const created = new Date(createdAt)
      if (!created || Number.isNaN(created.getTime())) return null
      const days = Math.floor((Date.now() - created.getTime()) / 86400000)
      if (days > 95) return '365d'
      if (days > 70) return '90d'
      if (days > 21) return '30d'
      if (days > 10) return '14d'
      return '7d'
    },
    maybeApplyDefaultRange() {
      if (this.dateRangeManuallySet) return
      const inferred = this.inferDefaultRangeFromAge(this.dataStartsAt)
      if (inferred && inferred !== this.dateRangeType) {
        this.dateRangeType = inferred
      }
    },
    onSortBySelect(value) {
      if (typeof value === 'string' && value) {
        this.sortBy = value
      }
      this.sortGames()
    },
    onGamesPerPageChange(value) {
      const parsed = Number(value)
      if (Number.isFinite(parsed) && parsed > 0) {
        this.gamesPerPage = parsed
      }
      this.resetPagination()
    },
    sanitizeSteamIdentifierInput(rawInput) {
      if (typeof rawInput !== 'string') {
        return ''
      }

      const trimmed = rawInput.trim()

      if (!trimmed) {
        return ''
      }

      const profileMatch = trimmed.match(/steamcommunity\.com\/profiles\/(\d{17})(?:[\/?#]|$)/i)

      if (profileMatch && profileMatch[1]) {
        return profileMatch[1]
      }

      const vanityMatch = trimmed.match(/steamcommunity\.com\/id\/([^/?#]+)(?:[\/?#]|$)/i)

      if (!vanityMatch || !vanityMatch[1]) {
        return ''
      }

      const rawSegment = vanityMatch[1]

      try {
        const decoded = decodeURIComponent(rawSegment)
        const sanitized = decoded.trim()
        return sanitized || ''
      } catch (_) {
        const fallback = (rawSegment || '').trim()
        return fallback || ''
      }
    },
    prepareSteamIdentifier(input) {
      const trimmed = typeof input === 'string' ? input.trim() : ''

      if (!trimmed) {
        return ''
      }

      const sanitized = this.sanitizeSteamIdentifierInput(trimmed)
      return sanitized || trimmed
    },
    async handleRouteChange(rawParam) {
      const rawTrimmed = typeof rawParam === 'string' ? rawParam.trim() : ''
      const identifier = this.prepareSteamIdentifier(rawParam)

      if (identifier && rawTrimmed && identifier !== rawTrimmed) {
        try {
          const navigated = await this.navigateToIdentifier(identifier)

          if (navigated) {
            return
          }
        } catch (error) {
          console.error('Error normalizing route identifier:', error)
        }
      }

      if (identifier === this.currentIdentifier) {
        if (!identifier) {
          await this.resetToLanding({ fetchStats: this.yesterdayTotalMinutes === null })
        }
        return
      }

      this.currentIdentifier = identifier
      this.pendingRouteUpdate = null
      this.logoutInProgress = false

      if (!identifier) {
        await this.resetToLanding({ fetchStats: true })
        return
      }

      try {
        await this.loadProfileForIdentifier(identifier)
      } catch (error) {
        console.error('Error handling route change:', error)
      }
    },

    async handleExternalLogout() {
      if (this.logoutInProgress) {
        return
      }

      this.logoutInProgress = true

      const currentParam = (this.$route && this.$route.params && typeof this.$route.params.steamId === 'string')
        ? this.$route.params.steamId.trim()
        : ''

      if (!currentParam) {
        this.currentIdentifier = ''
        try {
          await this.resetToLanding({ fetchStats: true })
        } finally {
          this.logoutInProgress = false
        }
        return
      }

      try {
        await this.navigateToIdentifier('')
      } catch (error) {
        console.error('Error returning to dashboard:', error)
        this.currentIdentifier = ''
        await this.resetToLanding({ fetchStats: true })
      } finally {
        this.logoutInProgress = false
      }
    },

    async navigateToIdentifier(identifier) {
      const target = this.prepareSteamIdentifier(identifier)
      const currentParam = (this.$route && this.$route.params && typeof this.$route.params.steamId === 'string')
        ? this.$route.params.steamId.trim()
        : ''

      if (target === currentParam) {
        return false
      }

      this.pendingRouteUpdate = target

      const routeConfig = target
        ? { name: 'Dashboard', params: { steamId: target } }
        : { name: 'DashboardHome' }

      try {
        await this.$router.replace(routeConfig)
      } catch (error) {
        this.pendingRouteUpdate = null
        throw error
      }

      return true
    },

    clearSyncPoll() {
      if (this.syncPollTimer) {
        clearTimeout(this.syncPollTimer)
        this.syncPollTimer = null
      }
    },

    async handlePrivateGameDetailsDuringSync() {
      const message = this.$t('errors.statsNotPublic')

      try {
        await this.navigateToIdentifier('')
      } catch (error) {
        //
      }

      try {
        await this.resetToLanding({ fetchStats: true })
      } catch (error) {
        //
      }

      this.steamError = message
      this.steamSuccess = null

      if (this.lastSteamIdentifier) {
        this.steamInput = this.lastSteamIdentifier
      }
    },

    startSyncPolling(steamId) {
      if (!steamId) return

      this.clearSyncPoll()
      this.syncWaitEnded = false

      const start = Date.now()
      const maxWaitMs = 5 * 60 * 1000

      const poll = async () => {
        if (!steamId || steamId !== this.selectedSteamId) {
          this.clearSyncPoll()
          return
        }

        try {
          const status = await playtimeAPI.getSyncStatus(steamId)
          const isSynced = Boolean(status.data?.isSynced)
          const isPublic = status.data?.isPublic

          if (isSynced && isPublic === false) {
            this.clearSyncPoll()
            this.syncWaitEnded = true
            await this.handlePrivateGameDetailsDuringSync()
            return
          }

          if (isSynced) {
            this.clearSyncPoll()
            // Steam has answered, so the wait is over whatever happens next.
            // Set BEFORE the refresh: a refresh that throws replaces the
            // profile with an empty one, which used to put isSynced back to
            // false and strand the overlay over data that had already loaded.
            this.syncWaitEnded = true
            try {
              await this.selectSteamProfile(steamId, { refreshProfile: true })
              this.steamSuccess = this.$t('status.profileReady')
            } catch (error) {
              //
            }
            return
          }
        } catch (_) {}

        if (Date.now() - start < maxWaitMs) {
          this.syncPollTimer = setTimeout(poll, 2500)
        } else {
          this.steamSuccess = this.$t('status.stillSyncing')
          this.clearSyncPoll()
          this.syncWaitEnded = true
        }
      }

      poll()
    },

    async loadProfileForIdentifier(identifier) {
      const input = this.prepareSteamIdentifier(identifier)
      if (!input) return

      this.lastSteamIdentifier = input

      const requestId = ++this.profileRequestId
      this.resolving = true
      this.error = null
      this.steamError = null
      this.steamSuccess = null
      this.clearSyncPoll()

      try {
        const profileResp = await playtimeAPI.getUserProfile(input, { login: !this.isBrowseVisit })
        if (requestId !== this.profileRequestId) {
          return
        }

        const data = profileResp?.data || {}
        const steamId = typeof data.steam_id === 'string' && data.steam_id ? data.steam_id : ''
        // Registration moved behind the claim on 2026-09-02: an unclaimed
        // profile answers with an empty shell and this flag.
        const registered = data.registered !== false

        this.profileRegistered = registered

        if (!steamId && !registered) {
          // A vanity nobody has claimed. There is no id to show a dashboard
          // for, and asking Steam for one is exactly the call this change
          // removed, so the gate opens on its own and the Steam round-trip
          // resolves the identity.
          this.mandatoryClaimPending = true
          this.mandatoryClaimSteamId = ''
          this.mandatoryClaimReason = 'new_lookup'
          this.steamInput = input
          trackEvent('mandatory_claim_shown', { reason: 'new_lookup' })
          this.loading = false

          return
        }

        if (!steamId) {
          const message = this.$t('errors.couldNotResolve')
          await this.resetToLanding({ fetchStats: true })
          this.steamError = message
          this.steamInput = input
          return
        }

        const needsSync = typeof data.isSynced === 'boolean' ? !data.isSynced : false

        // Strictly `false`: a payload cached before the API sent the field
        // has no `claimed` at all, and must not open the gate on a claimed
        // profile. The ClaimCard re-checks live and hides itself if claimed.
        // Not on a landing-page click: that visitor is not the owner, and
        // the gate is only for someone who searched or opened the URL.
        const unclaimed = data.claimed === false && !this.isBrowseVisit
        const pending = isMandatoryClaimPending(steamId)

        this.isNewLookup = needsSync
        if (needsSync || pending || unclaimed) {
          if (needsSync) {
            setMandatoryClaimPending(steamId, true)
          }
          let reason = 'unclaimed_profile'
          if (needsSync) reason = 'new_lookup'
          else if (pending) reason = 'pending_return'

          // The gate's own denominator: it arms only on a brand-new lookup, a
          // return to one that never finished, or an old never-claimed
          // profile — never for anyone viewing a claimed profile.
          if (!this.mandatoryClaimPending || this.mandatoryClaimSteamId !== steamId) {
            trackEvent('mandatory_claim_shown', { reason })
          }
          this.mandatoryClaimPending = true
          this.mandatoryClaimSteamId = steamId
          this.mandatoryClaimReason = reason
        } else {
          this.mandatoryClaimPending = false
          this.mandatoryClaimSteamId = ''
          this.mandatoryClaimReason = ''
        }
        this.userProfile = normalizeProfile(data)
        try {
          window.dispatchEvent(new CustomEvent('steamProfileUpdated', { detail: { profile: this.userProfile, steamId, source: 'dashboard' } }))
        } catch (_) {}

        this.selectedSteamId = steamId

        this.steamSuccess = needsSync
          ? this.$t('status.preparingProfile')
          : this.$t('status.profileFound')

        if (requestId !== this.profileRequestId) {
          return
        }

        // Unclaimed: there is no sync to poll and no data to preview. Stop here
        // with the gate up rather than starting a five-minute poll against a
        // job that was never enqueued.
        if (!registered) {
          this.loading = false

          return
        }

        if (!needsSync) {
          this.updateTopGamesPreview({ force: true })
        } else {
          this.clearTopGamesPreview()
        }

        if (needsSync) {
          this.loading = false
          this.startSyncPolling(steamId)
          return
        }

        if (requestId !== this.profileRequestId) {
          return
        }

        await this.selectSteamProfile(steamId)
      } catch (error) {
        if (requestId !== this.profileRequestId) {
          return
        }
        console.error('Error loading profile from route:', error)
        const message = error.response?.data?.error || this.$t('errors.failedToLoadProfile')
        await this.resetToLanding({ fetchStats: true })
        this.steamError = message
        this.steamInput = input
      } finally {
        if (requestId === this.profileRequestId) {
          this.resolving = false
        }
      }
    },

    computeBaseDateRange() {
      const trackingEnd = this.trackingEndDate
      let end = trackingEnd ? new Date(trackingEnd) : new Date()

      if (!end || Number.isNaN(end.getTime())) {
        return { start: null, end: null }
      }

      end.setUTCHours(0, 0, 0, 0)

      const backfillDays = days => {
        const value = new Date(end)
        value.setUTCDate(value.getUTCDate() - days)
        value.setUTCHours(0, 0, 0, 0)
        return value
      }

      let start = null

      switch (this.dateRangeType) {
        case 'thisWeek': {
          const tempEnd = new Date(end)
          const day = tempEnd.getUTCDay()
          const diffToMonday = day === 0 ? 6 : day - 1
          const monday = new Date(tempEnd)
          monday.setUTCDate(tempEnd.getUTCDate() - diffToMonday)
          monday.setUTCHours(0, 0, 0, 0)
          start = monday
          break
        }
        case 'thisMonth': {
          const tempEnd = new Date(end)
          start = new Date(Date.UTC(tempEnd.getUTCFullYear(), tempEnd.getUTCMonth(), 1))
          break
        }
        case '365d':
          start = backfillDays(364)
          break
        case '90d':
          start = backfillDays(89)
          break
        case '30d':
          start = backfillDays(29)
          break
        case '14d':
          start = backfillDays(13)
          break
        case '7d':
          start = backfillDays(6)
          break
        case 'all':
          start = null
          break
        default:
          start = backfillDays(89)
      }

      if (!start && end) {
        start = new Date(end)
        start.setUTCHours(0, 0, 0, 0)
      }

      if (start && end && start > end) {
        start = new Date(end)
        start.setUTCHours(0, 0, 0, 0)
      }

      return { start, end }
    },

    currentRangeParams() {
      const { start, end } = this.computeBaseDateRange()
      const params = {}
      let startKey = this.sanitizeDiffDate(start)
      let endKey = this.sanitizeDiffDate(end)
      // Floor the range at the start of the data we hold. For a returning
      // user that is their return date; clamping to created_at would request
      // months of deleted days.
      const createdFloor = this.sanitizeDiffDate(this.dataStartsAt)
      const todayCeil = this.todayISO

      if (createdFloor) {
        if (!startKey || startKey < createdFloor) {
          startKey = createdFloor
        }
        if (endKey && endKey < createdFloor) {
          endKey = createdFloor
        }
      }

      if (todayCeil) {
        if (startKey && startKey > todayCeil) {
          startKey = todayCeil
        }
        if (endKey && endKey > todayCeil) {
          endKey = todayCeil
        }
      }

      if (startKey && endKey && startKey > endKey) {
        endKey = startKey
      }

      if (startKey) params.startDate = startKey
      if (endKey) params.endDate = endKey
      return Object.keys(params).length ? params : undefined
    },
    async refreshPlaytime() {
      if (!this.selectedSteamId) return
      try {
        const params = this.currentRangeParams()
        
        this.effectiveRange = params
          ? { start: params.startDate || null, end: params.endDate || null }
          : { start: null, end: null }
        const response = await playtimeAPI.getPlaytimeDiffBySteamId(this.selectedSteamId, params)
        const payload = response.data || {}
        const items = Array.isArray(payload) ? payload : (payload.items || [])
        this.playtimeData = items
          .map(record => ({
            app_id: record.app_id,
            date: this.sanitizeDiffDate(record.date),
            playtime_forever: Number(record.minutes) || 0
          }))
          .filter(entry => entry.date)
        this.appNameById = payload.appNames || this.appNameById
        if (payload.appTypes && typeof payload.appTypes === 'object') {
          this.appTypes = payload.appTypes
        }
        if (payload.appImages && typeof payload.appImages === 'object') {
          this.appImagesById = payload.appImages
        }
        this.achievements = this.normalizeAchievements(payload.achievements)
        this.achievementsByApp = (payload.achievementsByApp && typeof payload.achievementsByApp === 'object') ? payload.achievementsByApp : {}
      } catch (e) {

        console.error('Error refreshing playtime with filters:', e)
      }
    },
    async fetchYesterdayTotals(opts = {}) {
      const force = !!opts.force

      if (!force && this.yesterdayTotalMinutes !== null) {
        return
      }

      if (!force && this.yesterdayTotalsPromise) {
        return this.yesterdayTotalsPromise
      }

      const task = (async () => {
        try {
          const response = await playtimeAPI.getYesterdayPlaytime()
          const data = response?.data || {}
          const minutesRaw = Number(data.totalMinutes)
          this.yesterdayTotalMinutes = Number.isFinite(minutesRaw) ? minutesRaw : 0
          this.yesterdayTotalDate = typeof data.date === 'string' ? data.date : ''
          const achRaw = Number(data.achievementsUnlocked)
          this.yesterdayAchievements = Number.isFinite(achRaw) ? achRaw : 0
          const uniqueRaw = Number(data.uniqueAppIds)
          this.yesterdayUniqueApps = Number.isFinite(uniqueRaw) ? uniqueRaw : 0
          this.yesterdayTopGames = Array.isArray(data.topGames) ? data.topGames : []
          this.yesterdayTopUsers = Array.isArray(data.topUsers) ? data.topUsers : []
          this.yesterdayTopAchievers = Array.isArray(data.topAchievers) ? data.topAchievers : []
        } catch (error) {
          console.error('Error fetching yesterday totals:', error)
          if (force || this.yesterdayTotalMinutes === null) {
            this.yesterdayTotalMinutes = null
            this.yesterdayTotalDate = ''
            this.yesterdayAchievements = null
            this.yesterdayUniqueApps = null
            this.yesterdayTopGames = []
            this.yesterdayTopUsers = []
            this.yesterdayTopAchievers = []
          }
        } finally {
          if (this.yesterdayTotalsPromise === task) {
            this.yesterdayTotalsPromise = null
          }
        }
      })()

      this.yesterdayTotalsPromise = task
      return task
    },

    async resolveSteamId() {
      const inputValue = this.prepareSteamIdentifier(this.steamInput)
      if (!inputValue) return

      this.steamError = null
      this.steamSuccess = null
      this.resolving = true
      this.clearSyncPoll()

      this.lastSteamIdentifier = inputValue
      const currentParam = (this.$route && this.$route.params && typeof this.$route.params.steamId === 'string')
        ? this.$route.params.steamId.trim()
        : ''

      this.steamInput = ''

      if (inputValue === currentParam) {
        this.currentIdentifier = inputValue
        this.pendingRouteUpdate = null
        this.logoutInProgress = false
        await this.loadProfileForIdentifier(inputValue)
        return
      }

      try {
        await this.navigateToIdentifier(inputValue)
      } catch (error) {
        console.error('Error navigating to profile route:', error)
        await this.loadProfileForIdentifier(inputValue)
      }
    },

    async selectSteamProfile(steamId, opts = {}) {
      if (!steamId) {
        this.error = this.$t('errors.invalidSelection')
        return
      }
      const refreshProfile = !!opts.refreshProfile
      try {
        this.loading = true
        this.selectedSteamId = steamId

        // Pick a sensible default range based on how long this user has
        // been tracked, before computing the first diff fetch params.
        // Skipped if the user has already manually changed the range.
        this.maybeApplyDefaultRange()

        const paramsBefore = this.currentRangeParams()
        const initialStart = paramsBefore?.startDate || null
        const initialEnd = paramsBefore?.endDate || null
        this.effectiveRange = { start: initialStart, end: initialEnd }

        const response = await playtimeAPI.getPlaytimeDiffBySteamId(steamId, paramsBefore)
        const payload = response.data || {}
        const items = Array.isArray(payload) ? payload : (payload.items || [])
        this.playtimeData = items
          .map(record => ({
            app_id: record.app_id,
            date: this.sanitizeDiffDate(record.date),
            playtime_forever: Number(record.minutes) || 0
          }))
          .filter(entry => entry.date)
        this.appNameById = payload.appNames || {}
        this.appTypes = (payload.appTypes && typeof payload.appTypes === 'object') ? payload.appTypes : {}
        this.appImagesById = (payload.appImages && typeof payload.appImages === 'object') ? payload.appImages : {}
        this.achievements = this.normalizeAchievements(payload.achievements)
        this.achievementsByApp = (payload.achievementsByApp && typeof payload.achievementsByApp === 'object') ? payload.achievementsByApp : {}

        this.effectiveRange = { start: initialStart, end: initialEnd }
        
        if (refreshProfile || !this.userProfile || this.userProfile.steam_id !== steamId) {
          try {
            const profile = await playtimeAPI.getUserProfile(steamId, { login: !this.isBrowseVisit })
            this.userProfile = normalizeProfile(profile.data)
            
            try {
              window.dispatchEvent(new CustomEvent('steamProfileUpdated', { detail: { profile: this.userProfile, steamId: this.selectedSteamId, source: 'dashboard' } }))
            } catch (_) {}
          } catch (e) {
            this.userProfile = normalizeProfile()
          }
        }

        this.updateTopGamesPreview({ force: refreshProfile })
      } catch (error) {
        console.error('Error loading playtime data:', error)
        this.error = this.$t('errors.failedToLoadPlaytime')
        this.selectedSteamId = ''
      } finally {
        this.loading = false
      }
    },

    updateTopGamesPreview(opts = {}) {
      const force = !!opts.force

      if (this.newlyRegistered) {
        this.maybeLoadTopGamesPreview(force).catch(() => {})
      } else {
        this.clearTopGamesPreview()
      }
    },

    async maybeLoadTopGamesPreview(force = false) {
      const steamId = typeof this.selectedSteamId === 'string' ? this.selectedSteamId.trim() : ''

      if (!steamId || !this.newlyRegistered) {
        this.clearTopGamesPreview()
        return
      }

      if (!force && this.topGamesPreview.loading && this.topGamesPreview.steamId === steamId) {
        return
      }

      if (!force && this.topGamesPreview.loaded && this.topGamesPreview.steamId === steamId && !this.topGamesPreview.error) {
        return
      }

      const requestId = ++this.topGamesPreviewRequestId
      this.topGamesPreview.loading = true
      this.topGamesPreview.error = null
      this.topGamesPreview.steamId = steamId

      try {
        const response = await playtimeAPI.getUserTopGames(steamId)
        if (requestId !== this.topGamesPreviewRequestId) {
          return
        }

        const payload = response?.data || {}
        const gamesRaw = Array.isArray(payload.games) ? payload.games : []

        this.topGamesPreview.games = gamesRaw.map(item => ({
          app_id: item.app_id,
          name: typeof item.name === 'string' && item.name.trim() ? item.name.trim() : null,
          minutes: Number(item.minutes) || 0
        }))
        const totalCount = Number(payload.totalGames)
        this.topGamesPreview.totalGames = Number.isFinite(totalCount) && totalCount >= this.topGamesPreview.games.length
          ? totalCount
          : this.topGamesPreview.games.length

        const ach = payload.achievements && typeof payload.achievements === 'object' ? payload.achievements : { total: 0, icons: [] }
        this.topGamesPreview.achievements = {
          total: Number(ach.total) || 0,
          icons: Array.isArray(ach.icons) ? ach.icons : []
        }

        this.topGamesPreview.loaded = true
      } catch (error) {
        if (requestId !== this.topGamesPreviewRequestId) {
          return
        }

        console.error('Error loading top games preview:', error)
        const serverMessage = error?.response?.data?.error
        this.topGamesPreview.error = typeof serverMessage === 'string' && serverMessage.trim()
          ? serverMessage.trim()
          : this.$t('errors.previewUnavailable')
        this.topGamesPreview.games = []
        this.topGamesPreview.totalGames = 0
        this.topGamesPreview.loaded = true
      } finally {
        if (requestId === this.topGamesPreviewRequestId) {
          this.topGamesPreview.loading = false
        }
      }
    },

    clearTopGamesPreview() {
      this.topGamesPreviewRequestId += 1
      this.topGamesPreview.loading = false
      this.topGamesPreview.error = null
      this.topGamesPreview.games = []
      this.topGamesPreview.totalGames = 0
      this.topGamesPreview.achievements = { total: 0, icons: [] }
      this.topGamesPreview.steamId = ''
      this.topGamesPreview.loaded = false
    },

    async resetToLanding(opts = {}) {
      const fetchStats = opts.fetchStats !== false

      this.clearSyncPoll()
      this.profileRequestId += 1
      this.currentIdentifier = ''
      this.pendingRouteUpdate = null
      this.logoutInProgress = false
      this.loading = false
      this.resolving = false
      this.error = null
      this.steamError = null
      this.steamSuccess = null
      this.selectedSteamId = ''
      this.isNewLookup = false
      this.profileRegistered = true
      this.mandatoryClaimPending = false
      this.mandatoryClaimSteamId = ''
      this.mandatoryClaimReason = ''
      this.playtimeData = []
      this.appNameById = {}
      this.appTypes = {}
      this.appImagesById = {}
      this.achievements = { unlockedCount: 0, items: [] }
      this.achievementsByApp = {}
      this.steamInput = ''
      this.userProfile = normalizeProfile()
      this.dateRangeManuallySet = false
      this.clearTopGamesPreview()

      if (fetchStats) {
        try {
          await this.fetchYesterdayTotals({ force: this.yesterdayTotalMinutes === null })
        } catch (_) {}
      }

      try {
        window.dispatchEvent(new CustomEvent('steamProfileUpdated', { detail: { profile: this.userProfile, steamId: '', source: 'dashboard' } }))
      } catch (_) {}
    },

    applyFilters() {
      
      this.resetPagination()
      
      this.suspendCharts = true
      
      this.$nextTick(() => this.refreshPlaytime()
        .catch(() => {})
        .finally(() => { this.suspendCharts = false }))
    },

    parseDateInput(value) {
      if (value === null || value === undefined) {
        return null
      }

      const buildResult = (date, localDate, offsetMinutes, hasTime, original = '') => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
          return null
        }
        const safeLocal = localDate instanceof Date && !Number.isNaN(localDate.getTime())
          ? localDate
          : new Date(date.getTime() + offsetMinutes * 60000)
        const year = safeLocal.getUTCFullYear()
        const month = String(safeLocal.getUTCMonth() + 1).padStart(2, '0')
        const day = String(safeLocal.getUTCDate()).padStart(2, '0')

        return {
          date,
          localDate: safeLocal,
          offsetMinutes,
          hasTime,
          original,
          dateKey: `${year}-${month}-${day}`
        }
      }

      if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null
        const utcDate = new Date(value.getTime())
        return buildResult(utcDate, new Date(value.getTime()), 0, true, value.toISOString())
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return this.parseDateInput(new Date(value))
      }

      if (typeof value !== 'string') {
        return null
      }

      const trimmed = value.trim()
      if (!trimmed) return null

      const dateOnlyMatch = trimmed.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/)
      if (dateOnlyMatch) {
        const [, yearStr, monthStr, dayStr] = dateOnlyMatch
        const year = Number.parseInt(yearStr, 10)
        const month = Number.parseInt(monthStr, 10) - 1
        const day = Number.parseInt(dayStr, 10)
        const utcMillis = Date.UTC(year, month, day, 0, 0, 0, 0)
        const date = new Date(utcMillis)
        const localDate = new Date(utcMillis)
        return buildResult(date, localDate, 0, false, trimmed)
      }

      const isoMatch = trimmed.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})(?:[T\s]([0-9]{2})(?::([0-9]{2})(?::([0-9]{2})(?:\.([0-9]{1,3}))?)?)?)?(Z|[+-][0-9]{2}:?[0-9]{2})?$/)
      if (isoMatch) {
        const [
          ,
          yearStr,
          monthStr,
          dayStr,
          hourStr,
          minuteStr,
          secondStr,
          fractionStr,
          zoneStr
        ] = isoMatch

        const year = Number.parseInt(yearStr, 10)
        const month = Number.parseInt(monthStr, 10) - 1
        const day = Number.parseInt(dayStr, 10)
        const hour = hourStr !== undefined ? Number.parseInt(hourStr, 10) : 0
        const minute = minuteStr !== undefined ? Number.parseInt(minuteStr, 10) : 0
        const second = secondStr !== undefined ? Number.parseInt(secondStr, 10) : 0
        const millisecond = fractionStr ? Number.parseInt(fractionStr.padEnd(3, '0').slice(0, 3), 10) : 0

        let offsetMinutes = 0
        if (zoneStr) {
          if (zoneStr.toUpperCase() === 'Z') {
            offsetMinutes = 0
          } else {
            const sign = zoneStr.startsWith('-') ? -1 : 1
            const digits = zoneStr.slice(1).replace(':', '')
            const hoursPart = Number.parseInt(digits.slice(0, 2) || '0', 10)
            const minutesPart = Number.parseInt(digits.slice(2) || '0', 10)
            offsetMinutes = sign * (hoursPart * 60 + minutesPart)
          }
        }

        const localUtcMillis = Date.UTC(year, month, day, hour, minute, second, millisecond)
        const utcMillis = localUtcMillis - offsetMinutes * 60000
        const date = new Date(utcMillis)
        const localDate = new Date(utcMillis + offsetMinutes * 60000)
        return buildResult(date, localDate, offsetMinutes, hourStr !== undefined, trimmed)
      }

      const parsedTs = Date.parse(trimmed)
      if (!Number.isNaN(parsedTs)) {
        const date = new Date(parsedTs)
        return buildResult(date, new Date(parsedTs), 0, true, trimmed)
      }

      return null
    },
    sanitizeDiffDate(value) {
      const parsed = this.parseDateInput(value)
      return parsed?.dateKey || ''
    },
    formatTimezoneOffset(offsetMinutes) {
      if (!Number.isFinite(offsetMinutes)) {
        return ''
      }
      if (offsetMinutes === 0) {
        return 'UTC+00:00'
      }
      const sign = offsetMinutes >= 0 ? '+' : '-'
      const absolute = Math.abs(offsetMinutes)
      const hours = Math.floor(absolute / 60)
      const minutes = absolute % 60
      const paddedHours = String(hours).padStart(2, '0')
      const paddedMinutes = String(minutes).padStart(2, '0')
      return `UTC${sign}${paddedHours}:${paddedMinutes}`
    },
    toUtcDateFromKey(key) {
      const iso = this.sanitizeDiffDate(key)
      if (!iso) return null
      const date = new Date(`${iso}T00:00:00Z`)
      if (!date || Number.isNaN(date.getTime())) {
        return null
      }
      return date
    },
    normalizeToUtcMidnight(input) {
      const date = this.toUtcDateFromKey(input)
      return date ? date.getTime() : null
    },
    normalizeToUtcDateKey(input) {
      return this.sanitizeDiffDate(input)
    },
    extractDateKey(value) {
      return this.sanitizeDiffDate(value)
    },
    calculateUtcDaySpan(start, end) {
      const startUtc = this.normalizeToUtcMidnight(start)
      const endUtc = this.normalizeToUtcMidnight(end)
      if (startUtc === null || endUtc === null) return 0
      const diff = endUtc - startUtc
      if (diff < 0) return 0
      return Math.floor(diff / 86400000) + 1
    },

    
    formatPlaytime(minutes) {
      if (minutes < 60) {
        return this.$t('common.minutesOnly', { m: minutes })
      }
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return remainingMinutes > 0
        ? this.$t('common.hoursMinutes', { h: hours, m: remainingMinutes })
        : this.$t('common.hoursOnly', { h: hours })
    },
    formatDate(value, options = {}) {
      const opts = typeof options === 'object' && options !== null ? options : {}
      const fallback = typeof opts.fallback === 'string' ? opts.fallback : ''
      const parsed = this.parseDateInput(value)
      if (!parsed) return fallback

      const locale = typeof opts.locale === 'string' ? opts.locale : (this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US')
      const variant = typeof opts.variant === 'string' ? opts.variant : 'long'
      const includeSeconds = !!opts.includeSeconds
      const includeTimezone = !!opts.includeTimezone
      const showTime = typeof opts.includeTime === 'boolean'
        ? opts.includeTime
        : parsed.hasTime

      let formatOptions
      switch (variant) {
        case 'axis':
          formatOptions = { month: 'short', day: 'numeric' }
          break
        case 'short':
          formatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
          break
        case 'long':
        default:
          formatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
          break
      }

      if (showTime) {
        formatOptions = {
          ...formatOptions,
          hour: '2-digit',
          minute: '2-digit'
        }
        if (includeSeconds) {
          formatOptions.second = '2-digit'
        }
      }

      const formatter = new Intl.DateTimeFormat(locale, {
        ...formatOptions,
        hour12: false,
        timeZone: 'UTC'
      })

      const formatted = formatter.format(parsed.localDate)
      if (includeTimezone) {
        const tzLabel = this.formatTimezoneOffset(parsed.offsetMinutes)
        return tzLabel ? `${formatted} ${tzLabel}` : formatted
      }

      return formatted
    },
    formatChartDateLabel(value) {
      const fallback = typeof value === 'string' ? value.trim() : ''
      return this.formatDate(value, { fallback, variant: 'axis', includeTime: false })
    },
    formatTimestamp(timestamp) {
      if (!timestamp || timestamp <= 0) return this.$t('common.never')
      try {
        return format(fromUnixTime(timestamp), 'MMM dd, yyyy HH:mm')
      } catch (e) {
        return this.$t('common.never')
      }
    },
    
    formatObj(d, pattern) {
      try {
        return format(d, pattern)
      } catch (e) {
        return ''
      }
    },
    
    formatHours(minutes) {
      return Math.round(minutes / 60 * 10) / 10
    },
    
    setSortBy(field) {
      if (this.sortBy === field) {
        this.toggleSortOrder()
      } else {
        this.sortBy = field
        this.sortOrder = 'desc'
      }
      this.resetPagination()
    },
    
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
    },
    
    sortGames() {
      
      this.$forceUpdate()
    },
    displayPlaytime(game) {
      return this.isAllRange ? game.total_playtime_db : game.daily_playtime
    },
    getComparablePlaytime(game) {
      return this.displayPlaytime(game)
    },
    
    getGameName(appId) {
      const name = this.appNameById?.[appId] || this.appNameById?.[String(appId)]
      return name || `Game ${appId}`
    },

    resolvePreviewGameName(game) {
      if (!game || typeof game !== 'object') {
        return ''
      }

      const rawName = typeof game.name === 'string' ? game.name.trim() : ''
      if (rawName) {
        return rawName
      }

      return this.getGameName(game.app_id)
    },
    
    
    
    getGameImageUrl(appId) {
      // Prefer the DB-sourced override (art under a different app_id or a
      // content-hash subpath, which the derived URL below would 404 on).
      // Fall back to the standard derived path for the ~80% of games where it's correct.
      return this.appImagesById[appId]
        || this.appImagesById[String(appId)]
        || `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${appId}/header.jpg`
    },
    normalizeAchievements(raw) {
      // Shape-guard the diff response's achievements card before handing it to
      // the component, so a missing/partial field never breaks rendering.
      return {
        unlockedCount: Number(raw?.unlockedCount) || 0,
        items: Array.isArray(raw?.items) ? raw.items : []
      }
    },
    getSteamStoreUrl(appId) {
      return `https://store.steampowered.com/app/${appId}`
    },
    
    
    
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    },
    
    resetPagination() {
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
.dashboard {
  max-width: 100%;
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.main-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
}

.content-area {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-width: 0;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: var(--text);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.error-container {
  max-width: 680px;
  margin: 20px auto;
  padding: 0 12px;
}

.alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
}

.alert-danger {
  background: rgba(217, 83, 79, 0.1);
  border: 1px solid rgba(217, 83, 79, 0.4);
  color: var(--text);
}

.alert-ico {
  font-size: 1.1rem;
}

.alert-text {
  flex: 1;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .content-area {
    gap: 16px;
  }
}
</style>

 
